"use client"

import { mriFromPrice } from "@/data/mri-packages"
import { useState, useEffect, useRef, useMemo } from "react"
import dynamic from "next/dynamic"
import { m, AnimatePresence } from "framer-motion"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { groupItemsByCmsCategory } from "@/lib/categories"
import {
  cmsMediaUrl,
  type CmsCategory,
  type CmsServiceConfig,
  type Testimonial,
} from "@/lib/cms"
import { Button } from "@/components/ui/button"
import { AddOnCard } from "@/components/ui/add-on-card"
import { BLOOD_ADDONS, bloodAddonPath } from "@/data/blood-addons"
import {
  ImagingAddonsSection,
  addonDisplayPrice,
} from "@/components/ui/imaging-addons-section"
import { InsuranceCoverageCta } from "@/components/marketing/insurance-coverage-cta"
import { CrossBorderNotice } from "@/components/ui/cross-border-notice"

import Image from "next/image"
import { cn } from "@/lib/utils"
import { pricingKeys as k } from "@/i18n/pricing-keys"
import { goKeys as gk } from "@/i18n/go-keys"
import {
  CheckCircleOutlineIcon,
  ChevronDownSmIcon,
  StarIcon,
} from "@/components/ui/icons"
import { fetchPrices, type PriceData } from "@/lib/pricing"
import { memberPriceLabelChf } from "@/lib/pricing/member-prices"
import { MriPackageCards } from "@/components/ui/mri-package-cards"
import { BloodTestCard } from "@/components/ui/blood-test-card"
import { BloodTrackingComparison } from "@/components/ui/blood-tracking-comparison"
import { CompareMriPackagesDropdown } from "@/components/ui/compare-mri-packages"
import { PersonPackageIcon } from "@/components/ui/package-icons"
import { TabSwitcher } from "@/components/ui/tab-switcher"
import { MembershipTabIcon } from "@/components/ui/membership-tab-icon"
import {
  ProductInfoButton,
  type BiomarkerGroup,
} from "@/components/ui/product-info-modal"
import { ADDON_INFO } from "@/data/product-info"
import { isAvailable } from "@/lib/availability"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { useCountry } from "@/components/layout/country-provider"
import { isAllowedInCountry } from "@/lib/country-gates"
import { isBookingOpen, isComingSoon, isWaitlistOnly } from "@/i18n/config"
import { ComingSoonPill } from "@/components/ui/coming-soon-pill"
// Code-split below-the-fold so its chunk doesn't enlarge the initial JS
// bundle. SSR is on so Googlebot sees the section content (previously
// `ssr: false` left an empty BAILOUT_TO_CLIENT_SIDE_RENDERING div in the
// prerendered HTML, hiding the whole membership pitch from search).
const HomepageMembershipSection = dynamic(() =>
  import("../../_components/homepage-membership-section").then(
    (m) => m.HomepageMembershipSection
  )
)

// Below-the-fold sections — code-split so their JS leaves the initial pricing
// chunk that has to be parsed/hydrated up front. Each sits after the first-
// viewport MRI package cards, so nothing above the fold waits on them. SSR
// stays on (next/dynamic default), so the prerendered HTML still carries every
// section and Googlebot sees them all — same trade as
// HomepageMembershipSection above. Trims first-party bootup work, which is what
// drags the mobile perf score (LCP is main-thread-bound here, not asset-bound).
//
// CompareMriPackagesDropdown and BloodTrackingComparison are deliberately left
// as static imports below: the pricing-page unit tests render this component
// synchronously and assert on their content, so deferring them would require a
// synchronous next/dynamic test mock. They can join this list once that mock
// lands.
// NO `loading` OPTION HERE, and no `ssr: false` — deliberately, same as
// HomepageMembershipSection above. Either one gives the lazy its own Suspense
// boundary (next's `hasSuspenseBoundary = !opts.ssr || !!opts.loading`), and a
// boundary in the initial tree is exactly what AHD-9096 removed from
// `[locale]/layout.tsx`: React stops waiting, emits `<!--$?-->` at the first
// one, and streams EVERYTHING after it — the rest of the page and the
// `<footer>` — into a hidden staging div revealed only by a trailing `$RC`.
// The first paint is then a bare shell, which is the flash AHD-9096 fixed.
//
// `loading: () => null` was added here once (#2736) to satisfy the
// dynamic-suspense guard, on the stated assumption that "the prerendered HTML
// is byte-identical". It is not: it moved `<footer>` from byte 155639 to after
// the placeholder at 102496 on /ch/en/pricing and reds `static-shell.spec.ts`.
// These three are unconditional below-the-fold sections in the initial render,
// so the guard's own allowlist is the right instrument — see the entry for
// this file in __tests__/dynamic-suspense-guard.test.ts. The code-split is
// unaffected: the chunk is still separate, which is all #2736 was after.
const QuizCtaBanner = dynamic(() =>
  import("@/components/marketing/quiz-cta-banner").then((m) => m.QuizCtaBanner)
)
const InsuranceSection = dynamic(() =>
  import("@/components/ui/insurance-section").then((m) => m.InsuranceSection)
)
const LogoMarquee = dynamic(() =>
  import("@/components/ui/logo-marquee").then((m) => m.LogoMarquee)
)

import { blobMediaUrl } from "@/lib/blob-url"

/** Slim CMS biomarker data passed from the server component */
export interface CmsBiomarkerSlim {
  name: string
  category: string
  packages: string[]
}

/** Group CMS biomarkers by category for a given package. Both the section
 * names and the display order come from the CMS `categories` collection
 * (sole source of truth, keyed by the granular category slug). */
function groupBiomarkersByCategory(
  biomarkers: CmsBiomarkerSlim[],
  packageId: string,
  categories: CmsCategory[]
): BiomarkerGroup[] {
  const filtered = biomarkers.filter((b) => b.packages.includes(packageId))
  return groupItemsByCmsCategory(filtered, categories).map(
    ({ category, items }) => ({
      category: category.name,
      items: items.map((b) => b.name),
    })
  )
}

/* TrustSignals imported from @/components/ui/mri-package-cards */

const LOGO_ITEMS = [
  { src: blobMediaUrl("/images/logos/unilabs.svg"), alt: "Unilabs", h: 28 },
  { src: blobMediaUrl("/images/logos/oxford.svg"), alt: "Oxford", h: 28 },
  { src: blobMediaUrl("/images/logos/google.svg"), alt: "Google", h: 28 },
  {
    src: blobMediaUrl("/images/logos/newzapiens.svg"),
    alt: "Newzapiens",
    h: 28,
  },
  {
    src: blobMediaUrl("/images/logos/siemens.svg"),
    alt: "Siemens Healthineers",
    h: 28,
  },
  { src: blobMediaUrl("/images/logos/quote.svg"), alt: "QUOTE", h: 28 },
  { src: blobMediaUrl("/images/logos/tum.svg"), alt: "TUM", h: 28 },
  { src: blobMediaUrl("/images/logos/techeu.svg"), alt: "tech.eu", h: 28 },
]

// Tier icons — extracted from Framer production site
export default function PricingClient({
  initialPrices = null,
  servicesConfig = [],
  cmsBiomarkers = [],
  cmsCategories = [],
  pricingTestimonial = null,
}: {
  initialPrices?: PriceData | null
  /** CMS products control plane — drives the imaging add-ons "Not available" pill. */
  servicesConfig?: CmsServiceConfig[]
  cmsBiomarkers?: CmsBiomarkerSlim[]
  cmsCategories?: CmsCategory[]
  pricingTestimonial?: Testimonial | null
}) {
  const countryConfig = useCountry()
  const country = countryConfig.code
  // DE: Germany sells the blood check-up and its blood add-ons, and nothing
  // else, so /pricing carries only those. The MRI packages, the imaging
  // add-ons and the membership block are dropped from the page entirely
  // rather than shown with a "Bald verfügbar" pill (the AHD-6680 treatment
  // this replaces): on a price list, three products the visitor cannot buy
  // read as the offer, and they push the one product Germany does sell down
  // the page. Everything gated on this flag comes back by deleting it once
  // DE_EXPANSION opens MRI + membership booking in Germany.
  // country-gate-ignore: DE checkup-phase page shape, not a page gate
  const bloodOnly = country === "de"
  // AT: the mirror image. Austria's catalogue is the three MRI packages —
  // Core, Advanced, Pro — and nothing else; the blood work it does sell is
  // bundled inside Advanced and Pro, never as its own SKU. So the blood half
  // of the page (the check-up card, its biomarker list, the blood add-ons)
  // goes, and the imaging half stays. Same reasoning as `bloodOnly`: a price
  // list is read as the offer, and a card for a product with no page behind it
  // is a 404 waiting to happen. Delete this once Austria sells a blood SKU.
  // country-gate-ignore: AT MRI-only page shape, not a page gate
  const mriOnly = country === "at"
  // DE's blood product is the Vorsorge-Starter, so its biomarker list is the
  // `prevention-starter` set (67), not the advanced blood test's.
  const bloodPackageId = bloodOnly ? "prevention-starter" : "blood"
  // Membership markets (CH + AT): the membership tab leads and the membership
  // section is hoisted above MRI/blood. Country-derived, so /pricing stays
  // edge-cacheable with no flag read. See lib/country-gates.ts.
  const isMembershipFirst = isAllowedInCountry(
    "feature:membership-first-positioning",
    country
  )
  // Trustpilot/Google reviews carry their text in `quote`; the short ones put
  // it in `headline` (with `quote` empty). Show whichever holds the text.
  const pricingTestimonialText =
    pricingTestimonial?.quote?.trim() || pricingTestimonial?.headline?.trim()
  // Land on the modality the market actually sells. Membership markets lead
  // with membership, but only once it is bookable — preselecting a tab whose
  // section says "coming soon" opens the page on the one thing nobody can buy.
  // Otherwise: MRI where MRI is bookable or where it is the whole catalogue
  // (AT), blood everywhere else (NL/DE). AHD-6370 / AHD-7869.
  const [activeTab, setActiveTab] = useState(
    isMembershipFirst && !bloodOnly && isBookingOpen(country, "membership")
      ? "membership"
      : mriOnly || isBookingOpen(country, "mri")
        ? "mri"
        : "blood"
  )
  const [biomarkersOpen, setBiomarkersOpen] = useState(false)
  const [liveP, setLiveP] = useState<PriceData | null>(initialPrices)
  const mriRef = useRef<HTMLElement>(null)
  const bloodRef = useRef<HTMLElement>(null)
  const membershipRef = useRef<HTMLDivElement>(null)
  const t = useTranslations("pricing")
  const ut = useTranslations("ui")
  // "for members" suffix on the priced add-ons — reuses the already-translated
  // go-namespace string the /go/bloodtest add-ons use, so no new key is added.
  const tGo = useTranslations("go")
  const locale = useLocale()
  const localePath = useLocalePath()

  const visibleBloodAddons = BLOOD_ADDONS.filter((a) =>
    isAvailable(ADDON_INFO[a.titleKey]?.availability, country, locale)
  )

  /** The add-on's (i) modal data, minus anything that talks about imaging.
   *  Every blood add-on closes its "what you get" list with
   *  `piAddTestsMriAnytime` ("Add additional tests or MRI imaging anytime"),
   *  which was the one MRI mention that survived inside a modal on the
   *  blood-only page. Dropped here rather than in `data/product-info.ts` so
   *  every other market keeps the line. The de-DE value has since lost its
   *  "MRT" too, because the quiz's modals render the list unfiltered. */
  function addonInfoFor(titleKey: string) {
    const info = ADDON_INFO[titleKey]
    if (!info || !bloodOnly) return info
    return {
      ...info,
      whatYouGetKeys: info.whatYouGetKeys?.filter(
        (key) => key !== "piAddTestsMriAnytime"
      ),
    }
  }

  /** Memoize biomarker groupings per package so they're only computed once */
  const biomarkerGroupsFor = useMemo(() => {
    if (cmsBiomarkers.length === 0) return () => undefined
    const cache = new Map<string, BiomarkerGroup[]>()
    return (packageId: string): BiomarkerGroup[] | undefined => {
      let groups = cache.get(packageId)
      if (!groups) {
        groups = groupBiomarkersByCategory(
          cmsBiomarkers,
          packageId,
          cmsCategories
        )
        cache.set(packageId, groups)
      }
      return groups.length > 0 ? groups : undefined
    }
  }, [cmsBiomarkers, cmsCategories])

  // Fetch live Stripe prices
  useEffect(() => {
    if (initialPrices) {
      setLiveP(initialPrices)
      return
    }
    fetchPrices(country).then(setLiveP)
  }, [country, initialPrices])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          // Scroll-spy lights a tab when its section scrolls into view. The
          // section exists (and the tab is clickable) when the category is
          // bookable OR coming-soon (DE checkup: MRI + membership render as
          // "Bald verfügbar"). Only a category with no section at all stays
          // unlit.
          if (
            entry.target.id === "mri-section" &&
            (isBookingOpen(country, "mri") || isComingSoon(country, "mri"))
          )
            setActiveTab("mri")
          else if (entry.target.id === "blood-section") setActiveTab("blood")
          else if (
            entry.target.id === "membership-section" &&
            (isBookingOpen(country, "membership") ||
              isComingSoon(country, "membership"))
          )
            setActiveTab("membership")
        })
      },
      { rootMargin: "-200px 0px -50% 0px", threshold: 0 }
    )
    if (mriRef.current) observer.observe(mriRef.current)
    if (bloodRef.current) observer.observe(bloodRef.current)
    if (membershipRef.current) observer.observe(membershipRef.current)
    return () => observer.disconnect()
    // Re-attach on country change: the membership section is re-parented
    // (bottom → top) in membership markets, so the observer must re-observe the
    // current section nodes.
  }, [isMembershipFirst, country])

  function handleTabChange(value: string) {
    setActiveTab(value)
    const id =
      value === "mri"
        ? "mri-section"
        : value === "blood"
          ? "blood-section"
          : "membership-section"
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  // Tab definitions extracted so membership markets can lead with the
  // membership tab without duplicating the JSX.
  // In `checkup` markets (DE) the MRI + membership products aren't bookable
  // yet. Rather than a dead greyed tab, they stay clickable (they scroll to
  // their on-page "Bald verfügbar" section) and carry a coming-soon pill so the
  // "not yet available" state is explicit. A tab is only truly disabled when
  // its section doesn't render at all (neither bookable nor coming-soon). AHD-6680.
  const mriEnabled = isBookingOpen(country, "mri")
  const membershipEnabled = isBookingOpen(country, "membership")
  const mriComingSoon = isComingSoon(country, "mri")
  const membershipComingSoon = isComingSoon(country, "membership")
  // Whether membership appears on this page at all: only where the market
  // sells it (page:membership). NL in `checkup` counts membership as
  // coming-soon, but it isn't offered there at all, so the tab and the
  // CH-priced pitch are dropped rather than promised (AHD-9361). The
  // blood-only page shape drops it too.
  const showMembershipSurfaces =
    !bloodOnly &&
    isAllowedInCountry("page:membership", country) &&
    (membershipEnabled || membershipComingSoon)
  const comingSoonBadge = <ComingSoonPill label={ut("comingSoon")} />
  const mriTab = {
    value: "mri",
    disabled: !mriEnabled && !mriComingSoon,
    badge: mriComingSoon ? comingSoonBadge : undefined,
    label: (
      <>
        <span className="md:hidden">{t(k.tabMriLabelShort)}</span>
        <span className="hidden md:inline">{t(k.tabMriLabel)}</span>
      </>
    ),
    icon: (
      <span className="bg-light-blue-300 flex size-[27px] items-center justify-center rounded-full">
        <PersonPackageIcon size={18} className="text-black" />
      </span>
    ),
  }
  const bloodTab = {
    value: "blood",
    label: (
      <>
        <span className="md:hidden">{t(k.tabBloodLabelShort)}</span>
        <span className="hidden md:inline">{t(k.tabBloodLabel)}</span>
      </>
    ),
    image: blobMediaUrl("/images/homepage/service-blood-cells.jpeg"),
  }
  const membershipTab = {
    value: "membership",
    disabled: !membershipEnabled && !membershipComingSoon,
    badge: membershipComingSoon ? comingSoonBadge : undefined,
    label: (
      <>
        <span className="md:hidden">{t(k.tabMembershipLabelShort)}</span>
        <span className="hidden md:inline">{t(k.tabMembershipLabel)}</span>
      </>
    ),
    icon: <MembershipTabIcon />,
  }
  // The single-modality pages (blood-only DE, MRI-only AT) drop the blood tab
  // or the MRI tab with the section it points at; where that leaves one tab the
  // switcher itself is dropped below rather than rendered as a single lit pill.
  // Arm leads with membership; control keeps MRI → blood → membership.
  const tabItems = (
    bloodOnly
      ? [bloodTab]
      : mriOnly
        ? [membershipTab, mriTab]
        : isMembershipFirst
          ? [membershipTab, mriTab, bloodTab]
          : [mriTab, bloodTab, membershipTab]
  ).filter((tab) => tab.value !== "membership" || showMembershipSurfaces)

  // The dedicated membership section (the full-bleed closing block in
  // testing-first markets) is hoisted to lead the page in membership markets.
  // Render it in exactly one place so the #membership-section anchor + ref stay
  // unique.
  const membershipSection = (
    <div
      id="membership-section"
      ref={membershipRef}
      className="scroll-mt-[140px]"
    >
      <HomepageMembershipSection fullBleed />
    </div>
  )

  return (
    <>
      {/* Sticky tab bar — only where there is something to switch between. A
          one-tab switcher is a label that looks clickable. */}
      {tabItems.length > 1 && (
        <div className="pointer-events-none sticky top-[72px] z-40 pt-2 pb-2 text-center">
          <div className="px-4 md:px-10">
            <div className="pointer-events-auto inline-flex max-w-full">
              <TabSwitcher
                tabs={tabItems}
                activeTab={activeTab}
                onChange={handleTabChange}
                // Mobile: drop the icon slot + tighten button padding so the
                // three short labels fit on a 390px viewport without scrolling.
                className="max-md:[&_button]:px-3 max-md:[&_button>.shrink-0]:hidden max-md:[&_button>img]:hidden"
              />
            </div>
          </div>
        </div>
      )}

      {/* Divider. The 100px sits under the sticky tab bar; with no bar to sit
          under it would be a 100px hole between the hero and the first
          section. */}
      <div
        className={cn(
          "mx-auto max-w-[1440px] px-4 md:px-10",
          tabItems.length > 1 ? "mt-[100px]" : "mt-8"
        )}
      >
        <hr className="border-neutral-200" />
      </div>

      {/* Membership leads the page in membership markets. */}
      {isMembershipFirst && showMembershipSurfaces && (
        <div className="pt-6">{membershipSection}</div>
      )}

      {/* Imaging + blood sections. The blood-only page renders none of the
          imaging half, so it starts at the blood section below. */}
      <div>
        {!bloodOnly && (
          <>
            {/* MRI pricing cards */}
            <section
              id="mri-section"
              ref={mriRef}
              className="scroll-mt-[140px] pt-12"
            >
              <div className="mx-auto max-w-[1440px] px-4 md:px-10">
                <h2 className="type-display-sm text-neutral-900">
                  {t(k.mriHeading)}
                </h2>
                <p className="type-body-lg mt-3 max-w-xl text-neutral-600">
                  {t(k.mriDescription, {
                    price: liveP ? mriFromPrice(liveP, country) : "",
                  })}
                </p>

                <div className="mt-10">
                  {/* The heading above is left-aligned and full width, so a
                      two-card market (Austria) spreads its cards across the
                      same width as the three Swiss cards instead of a centred
                      960px column. */}
                  <MriPackageCards
                    prices={liveP}
                    fetchIfMissing={false}
                    biomarkerGroupsFor={biomarkerGroupsFor}
                    className="mx-0 max-w-none"
                  />
                </div>
              </div>
            </section>

            {/* Section 5: Compare packages */}
            <CompareMriPackagesDropdown
              secondaryButton={
                // On mobile (stacked) Insurance coverage reads first; md+
                // reverts to source order so Compare sits on the left.
                // hover:bg-transparent overrides the default neutral-50 hover —
                // we want this CTA to read as an inline link, not a button.
                <InsuranceCoverageCta
                  size="md"
                  icon="heart-check-pulse"
                  className="order-1 hover:bg-transparent md:order-2"
                />
              }
            />

            {/* Section 6: Imaging add-ons */}
            <ImagingAddonsSection
              initialPrices={initialPrices}
              servicesConfig={servicesConfig}
              footnote={t(k.imagingAddonsPreConsultNote)}
            />
          </>
        )}

        {/* Quiz CTA — drives undecided users to the personalised quiz. Outside
            the imaging gate: it sells the funnel, not the scan, and carries
            its own country gate (section:marketing.quiz-cta-banner). */}
        <section className="px-4 md:px-10">
          <div className="mx-auto max-w-[1440px]">
            <QuizCtaBanner variant="compact" />
          </div>
        </section>

        {/* The blood half of the page. Austria sells no blood SKU — the
            work bundled inside Advanced and Pro is not a product you can
            buy on its own — so the check-up card, its biomarker list and
            the blood add-ons all go rather than stand as an offer the
            visitor cannot take up. */}
        {!mriOnly && (
          <>
            {/* Section 8: Blood tests */}
            <section
              id="blood-section"
              ref={bloodRef}
              className="scroll-mt-[140px] py-16"
            >
              <div className="mx-auto max-w-[1440px] px-4 md:px-10">
                {/* Heading + description inset to match the 960px card width
                below so the type aligns with the card edge instead of the
                wider 1440 container. */}
                <div className="mx-auto max-w-[960px]">
                  <h2 className="type-display-sm text-neutral-900">
                    {t(k.bloodHeading)}
                  </h2>
                  <p className="type-body-lg mt-3 max-w-xl text-neutral-600">
                    {t(k.bloodDescription)}
                  </p>
                </div>

                {/* One card, not a comparison, in the two markets with nothing to
                compare against: a waitlist market pre-launch (NL) and the
                blood-only page (DE), whose membership column doesn't exist.
                `BloodTrackingComparison` already collapses to this same card
                where membership booking is closed — going straight to it is
                what lets the page pass the two blood-only overrides below.
                Everywhere else: the membership-vs-one-off comparison (AHD-6471),
                same choice as /go/bloodtest. */}
                {isWaitlistOnly(country) || bloodOnly ? (
                  <div className="mx-auto mt-6 max-w-[960px]">
                    <BloodTestCard
                      trackingLocation="pricing-blood"
                      prices={liveP}
                      fetchIfMissing={false}
                      // The card's own (i) modal ends on "add tests or MRI
                      // imaging anytime" (`piAddTestsMriAnytime`), so the
                      // blood-only page keeps the button off and leaves the
                      // biomarker list below as the way in to the detail.
                      showInfoButton={!bloodOnly}
                      // On DE the card itself renders the /go/bloodtest version
                      // (trust labels, icons, description and "includes" list),
                      // so nothing is overridden here. The first bullet still
                      // names the prevention-starter count.
                      bloodFeatureLabel={
                        bloodOnly
                          ? t(k.preventionStarterCardFeature1)
                          : undefined
                      }
                      bloodBiomarkerGroups={biomarkerGroupsFor(bloodPackageId)}
                    />
                    {/* Cross-border footnote — it sat under the MRI cards, which
                    the blood-only page no longer has. Regulatory micro-copy for
                    the German market, so it stays, next to the one product on
                    the page. */}
                    {bloodOnly && (
                      <div className="mt-4 text-center">
                        <CrossBorderNotice variant="pricing-footnote" />
                      </div>
                    )}
                  </div>
                ) : (
                  <BloodTrackingComparison
                    className="mx-auto mt-6 max-w-[960px]"
                    trackingLocation="pricing-blood"
                  />
                )}

                {/* Biomarkers expandable — only when CMS data is available */}
                {biomarkerGroupsFor(bloodPackageId) && (
                  <>
                    <div className="flex flex-wrap items-center justify-center gap-3 px-4 py-8">
                      <Button
                        type="button"
                        variant="outline"
                        shape="round"
                        onClick={() => setBiomarkersOpen(!biomarkersOpen)}
                        // Mobile-first ordering: Insurance coverage above,
                        // biomarker list below. md+ reverts to source order.
                        className="group order-2 md:order-1"
                        iconRight={
                          <ChevronDownSmIcon
                            className={cn(
                              "transition-transform duration-200",
                              biomarkersOpen
                                ? "rotate-180"
                                : "group-hover:translate-y-0.5"
                            )}
                          />
                        }
                      >
                        {t(k.viewBiomarkersList)}
                      </Button>
                      <InsuranceCoverageCta
                        size="md"
                        icon="heart-check-pulse"
                        className="order-1 hover:bg-white md:order-2"
                      />
                    </div>

                    {/* safe-animate-presence: conditional-mount — gated by `biomarkersOpen=true` */}
                    <AnimatePresence>
                      {biomarkersOpen && (
                        <m.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: "easeOut" }}
                          className="overflow-hidden"
                        >
                          <div className="mx-auto max-w-4xl pb-6">
                            <div className="grid grid-cols-1 gap-x-12 gap-y-6 md:grid-cols-2">
                              {biomarkerGroupsFor(bloodPackageId)!.map(
                                (cat) => (
                                  <div key={cat.category}>
                                    <p className="type-title-sm mb-2 text-neutral-900">
                                      {cat.category}
                                    </p>
                                    <ul className="space-y-1">
                                      {cat.items.map((name) => (
                                        <li
                                          key={name}
                                          className="type-body-md flex items-center gap-2 text-neutral-600"
                                        >
                                          <CheckCircleOutlineIcon
                                            size={14}
                                            className="text-light-green-500 flex-shrink-0"
                                          />
                                          {name}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        </m.div>
                      )}
                    </AnimatePresence>
                  </>
                )}
              </div>
            </section>

            {/* Section 9: Blood add-ons */}
            <section className="py-16">
              <div className="mx-auto max-w-[1440px] px-4 md:px-10">
                <div>
                  <h2 className="type-display-sm text-neutral-900">
                    {t(k.bloodAddonsHeading)}
                  </h2>
                  <p className="type-body-lg mt-3 max-w-xl text-neutral-600">
                    {t(k.bloodAddonsDescription)}
                  </p>
                </div>
                <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2">
                  {visibleBloodAddons.map((a) => (
                    <div key={a.titleKey}>
                      <div className="relative">
                        <AddOnCard
                          title={t(a.titleKey)}
                          description={t(a.descKey)}
                          price={addonDisplayPrice(
                            liveP?.fullPrices[a.titleKey],
                            a.titleKey,
                            countryConfig.code
                          )}
                          memberPrice={
                            countryConfig.currency === "CHF"
                              ? (memberPriceLabelChf(
                                  liveP?.fullPrices ?? {},
                                  a.titleKey
                                ) ?? undefined)
                              : undefined
                          }
                          memberLabel={tGo(gk.bloodtestAddonsMemberSuffix)}
                          imageSrc={a.imageSrc}
                          href={
                            bloodAddonPath(a, country)
                              ? localePath(bloodAddonPath(a, country)!)
                              : undefined
                          }
                          // Waitlist market: add-ons show their price but aren't
                          // bookable yet — say so (2026-07-29 decision).
                          badge={
                            isWaitlistOnly(country) ? (
                              <ComingSoonPill label={ut("comingSoon")} />
                            ) : undefined
                          }
                        />
                        {ADDON_INFO[a.titleKey] && (
                          <div className="absolute top-3 right-3">
                            <ProductInfoButton
                              data={addonInfoFor(a.titleKey)!}
                              biomarkerGroups={
                                ADDON_INFO[a.titleKey].cmsPackageId
                                  ? biomarkerGroupsFor(
                                      ADDON_INFO[a.titleKey].cmsPackageId!
                                    )
                                  : undefined
                              }
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </>
        )}
      </div>

      {/* Insurance section — hidden on DE (the Selbstzahler module is dropped
          from the German pricing page for now). AHD-6680.
          country-gate-ignore: DE checkup-phase content hide, not a page gate */}
      {country !== "de" && (
        <InsuranceSection trackingLocation="pricing-insurance" />
      )}

      {/* Logo bar marquee */}
      <section className="border-t border-neutral-200 py-10">
        <LogoMarquee
          items={LOGO_ITEMS}
          theme="light"
          duration={30}
          gapClassName="gap-16"
        />
      </section>

      {/* Section 11: Testimonial — the CMS testimonial pinned to `/pricing`
          (fetched server-side in page.tsx). No pin (or no translation for the
          current locale) → the section is omitted entirely; there is no
          hardcoded fallback. Swap it by re-pinning a different testimonial in
          Payload.

          Dropped outright on both single-modality pages. The testimonial
          currently pinned to `/pricing` is a Zurich MRT scan ("Ich bin nach
          Zürich geflogen, um die MRT und die Blutanalyse bei Ahead zu machen").
          On DE that is a product Germany does not sell; on AT it is a product
          Austria does not sell either, plus another market's city on a page
          that is otherwise careful never to name one — the same class of claim
          `lint:market-facts` blocks in the message catalogs, which it cannot
          see here because this string lives in the CMS.

          Testimonials have no `hideInCountries` field (unlike FAQ items, blog
          posts and biomarkers), so an editor's only levers are the per-locale
          translation and `excludeFromPages`. Until one of those is used, or
          the collection grows a market hide, the page has to decide. The real
          fix is content: pin a market-appropriate testimonial per market, then
          DELETE THIS BRANCH — otherwise DE and AT will keep hiding one that is
          by then perfectly fine. */}
      {!bloodOnly &&
        !mriOnly &&
        pricingTestimonial &&
        pricingTestimonialText && (
          <section className="py-16 text-center">
            <div>
              <div className="mx-auto max-w-3xl">
                {pricingTestimonial.rating ? (
                  <div className="flex justify-center gap-1">
                    {Array.from(
                      { length: pricingTestimonial.rating },
                      (_, i) => (
                        <StarIcon key={i} width={20} height={20} />
                      )
                    )}
                  </div>
                ) : null}
                <blockquote className="type-body-lg mt-6 text-neutral-700 italic">
                  {pricingTestimonialText}
                </blockquote>
                <div className="mt-4 flex items-center justify-center gap-3">
                  {pricingTestimonial.avatar?.url ? (
                    <Image
                      src={cmsMediaUrl(pricingTestimonial.avatar.url)}
                      alt={pricingTestimonial.authorName}
                      width={32}
                      height={32}
                      className="h-8 w-8 rounded-full object-cover"
                    />
                  ) : null}
                  <span className="type-title-sm text-neutral-900">
                    {pricingTestimonial.authorName}
                  </span>
                </div>
              </div>
            </div>
          </section>
        )}

      {/* Final CTA — Ahead membership, full-bleed (matches the /go/membership +
          /go/bloodtest closing block). The wrapper div carries the membership
          tab's scroll anchor + IntersectionObserver target.
          Testing-first markets only; membership markets hoist this section to
          the top of the page (rendered after the divider above).
          Markets that neither sell membership nor advertise it as coming-soon
          (NL), and the blood-only page (DE), render no membership section at
          all. AHD-9361. */}
      {showMembershipSurfaces && !isMembershipFirst && membershipSection}
    </>
  )
}
